import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { documentService } from '../../services/documentService';
import { DOCUMENT_TYPES } from '../../config';

const ViewEstimatePage = () => {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [estimate, setEstimate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', customerName: '' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  useEffect(() => {      const fetchEstimateDetails = async () => {
      setIsLoading(true);
      setError('');
      try {
        const response = await fetch(`/api/estimates/${estimateId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            let errorMessage = `HTTP error! status: ${response.status}`;
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || errorMessage;
            } catch (jsonError) {
              console.error('Failed to parse error response as JSON:', jsonError);
            }
            throw new Error(errorMessage);
        }

        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error('Failed to parse response as JSON:', jsonError);
          throw new Error('Server returned invalid response format');
        }

        if (data.status === 'success') {
            setEstimate(data.data.estimate);
        } else {
            throw new Error(data.message || `Failed to parse estimate data for ID ${estimateId}`);
        }

      } catch (err) {
        console.error(`Error fetching estimate ${estimateId}:`, err);
        setError(err.message || `Could not load estimate ${estimateId}.`);
      } finally {
        setIsLoading(false);
      }
    };

    if (token && estimateId) {
      fetchEstimateDetails();
    }
  }, [estimateId, token]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleDateString();
    } catch (e) {
        return dateString;
    }
  };

  // Document handling functions
  const handleDownloadPdf = async () => {
    setIsGeneratingDocument(true);
    setDocumentError('');
    try {
      await documentService.downloadPdfFile(DOCUMENT_TYPES.ESTIMATE, estimateId, `Estimate-${estimate.estimateNumber || estimateId}.pdf`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setDocumentError('Failed to download PDF. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
      setShowPrintMenu(false);
    }
  };

  const handleViewPdf = () => {
    try {
      documentService.viewPdfInNewWindow(DOCUMENT_TYPES.ESTIMATE, estimateId);
      setShowPrintMenu(false);
    } catch (error) {
      console.error('Error viewing PDF:', error);
      setDocumentError('Failed to open PDF viewer. Please try again.');
    }
  };

  const handlePreviewHtml = async () => {
    setIsGeneratingDocument(true);
    setDocumentError('');
    try {
      await documentService.previewInPopup(DOCUMENT_TYPES.ESTIMATE, estimateId);
    } catch (error) {
      console.error('Error previewing HTML:', error);
      setDocumentError('Failed to generate HTML preview. Please try again.');
    } finally {
      setIsGeneratingDocument(false);
      setShowPrintMenu(false);
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    setIsSendingEmail(true);
    setDocumentError('');
    setEmailSuccess('');
    
    try {
      await documentService.sendEmail(
        DOCUMENT_TYPES.ESTIMATE,
        estimateId,
        emailData.email || estimate.client?.email,
        emailData.customerName || estimate.client?.username
      );
      setEmailSuccess('Estimate sent successfully!');
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSuccess('');
        setEmailData({ email: '', customerName: '' });
      }, 2000);
    } catch (error) {
      console.error('Error sending email:', error);
      setDocumentError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (isLoading) {
    return <div className="container mx-auto px-4 py-8 text-center"><p>Loading estimate details...</p></div>;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Link to="/admin/estimates" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Estimates</Link>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Link to="/admin/estimates" className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back to Estimates</Link>
        <p>No estimate data found.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <header className="mb-6">
        <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-brand-text-dark">Estimate Details: {estimate.id}</h1>
            <Link to="/admin/estimates" className="text-sm text-blue-600 hover:text-blue-800">&larr; Back to All Estimates</Link>
        </div>
      </header>

      <div className="bg-white shadow-lg rounded-lg p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h2 className="text-sm font-medium text-gray-500">Customer Name</h2>
            <p className="mt-1 text-lg text-gray-900">{estimate.client?.username || estimate.customerName || 'N/A'}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Customer Email</h2>
            <p className="mt-1 text-lg text-gray-900">{estimate.client?.email || estimate.customerEmail || 'N/A'}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Date</h2>
            <p className="mt-1 text-lg text-gray-900">{formatDate(estimate.estimateDate || estimate.date)}</p>
          </div>
          <div>
            <h2 className="text-sm font-medium text-gray-500">Status</h2>
            <p className="mt-1 text-lg">
              <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${ 
                estimate.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                estimate.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                estimate.status === 'Accepted' ? 'bg-green-100 text-green-800' :
                estimate.status === 'Declined' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {estimate.status}
              </span>
            </p>
          </div>
        </div>

        <div className="mb-6">
          <h2 className="text-sm font-medium text-gray-500">Description / Scope of Work</h2>
          <p className="mt-1 text-gray-700 whitespace-pre-wrap">{estimate.notes || estimate.description || 'No description provided.'}</p>
        </div>
        
        {estimate.lineItems && estimate.lineItems.length > 0 ? (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item/Service</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {estimate.lineItems.map((item, index) => (
                    <tr key={index}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">{item.description}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">{item.quantity}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 text-right">${parseFloat(item.unitPrice || 0).toFixed(2)}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 text-right">${parseFloat(item.total || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : <p className="text-gray-500">No line items found for this estimate.</p> }

        <div className="mt-8 border-t pt-6">
          <div className="flex justify-end items-center">
            <span className="text-lg font-medium text-gray-500 mr-2">Total:</span>
            <span className="text-2xl font-bold text-gray-900">${parseFloat(estimate.totalAmount || estimate.total || 0).toFixed(2)}</span>
          </div>
        </div>

        <div className="mt-8 flex justify-end space-x-3">
          <div className="relative inline-block">
            <button 
              onClick={() => setShowPrintMenu(!showPrintMenu)} 
              className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out flex items-center"
              disabled={isGeneratingDocument}
            >
              {isGeneratingDocument ? 'Processing...' : 'Print Estimate'}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
            
            {showPrintMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                <div className="py-1">
                  <button 
                    onClick={handleDownloadPdf}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Download PDF
                  </button>
                  <button 
                    onClick={handleViewPdf}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    View PDF in Browser
                  </button>
                  <button 
                    onClick={handlePreviewHtml}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Preview HTML
                  </button>
                  <button 
                    onClick={() => window.print()}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Print Current Page
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button 
            onClick={() => {
              setEmailData({ 
                email: estimate.client?.email || '', 
                customerName: estimate.client?.username || '' 
              });
              setShowEmailModal(true);
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
          >
            Send Email
          </button>
          
          {documentError && (
            <div className="w-full mt-2 text-red-600 text-sm">
              {documentError}
            </div>
          )}
          
          {emailSuccess && (
            <div className="w-full mt-2 text-green-600 text-sm">
              {emailSuccess}
            </div>
          )}
          
          {estimate.status !== 'Accepted' && estimate.status !== 'Converted' && (
            <button 
              onClick={() => navigate(`/admin/estimates/edit/${estimate.id}`)} 
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
            >
              Edit Estimate
            </button>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Send Estimate via Email</h3>
              <form onSubmit={handleSendEmail}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Email *
                  </label>
                  <input
                    type="email"
                    value={emailData.email}
                    onChange={(e) => setEmailData({...emailData, email: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={estimate.client?.email || "customer@example.com"}
                    required
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name
                  </label>
                  <input
                    type="text"
                    value={emailData.customerName}
                    onChange={(e) => setEmailData({...emailData, customerName: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={estimate.client?.username || "Customer Name"}
                  />
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEmailModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition duration-150 ease-in-out"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSendingEmail}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition duration-150 ease-in-out"
                  >
                    {isSendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewEstimatePage; 